import React, { useRef, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Share2, Download, X, Sparkles } from 'lucide-react';
import { gameBrand } from '../config/brand';

import propertiesData from '../data/properties.json';

export default function StoryCardModal({ gameData, onClose }) {
  const canvasRef = useRef(null);
  const [dataUrl, setDataUrl] = useState(null);

  useEffect(() => {
    if (!gameData || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const width = 1080;
    const height = 1920;
    canvas.width = width;
    canvas.height = height;

    // 1. Background Gradient
    const bgGrad = ctx.createLinearGradient(0, 0, 0, height);
    bgGrad.addColorStop(0, '#090d16');
    bgGrad.addColorStop(0.35, '#0f172a');
    bgGrad.addColorStop(0.7, '#1e1b4b');
    bgGrad.addColorStop(1, '#05070d');
    ctx.fillStyle = bgGrad;
    ctx.fillRect(0, 0, width, height);

    // Decorative Ambient Radial Glows
    const glow1 = ctx.createRadialGradient(width / 2, 400, 50, width / 2, 400, 500);
    glow1.addColorStop(0, 'rgba(212, 175, 55, 0.25)');
    glow1.addColorStop(1, 'rgba(212, 175, 55, 0)');
    ctx.fillStyle = glow1;
    ctx.fillRect(0, 0, width, height);

    const glow2 = ctx.createRadialGradient(width / 2, 1400, 50, width / 2, 1400, 600);
    glow2.addColorStop(0, 'rgba(14, 165, 233, 0.15)');
    glow2.addColorStop(1, 'rgba(14, 165, 233, 0)');
    ctx.fillStyle = glow2;
    ctx.fillRect(0, 0, width, height);

    // 2. Top Header Brand
    ctx.textAlign = 'center';
    
    // Subtitle
    ctx.font = 'bold 36px sans-serif';
    ctx.fillStyle = '#38bdf8';
    ctx.fillText('🌊 İZMİR EMLAK KRALLIĞI 🎲', width / 2, 140);

    // Main Title
    ctx.font = '900 80px sans-serif';
    const titleGrad = ctx.createLinearGradient(width / 2 - 250, 0, width / 2 + 250, 0);
    titleGrad.addColorStop(0, '#fbbf24');
    titleGrad.addColorStop(0.5, '#fff');
    titleGrad.addColorStop(1, '#f59e0b');
    ctx.fillStyle = titleGrad;
    ctx.fillText(gameBrand.displayName.toUpperCase(), width / 2, 230);

    // Header Divider Line
    ctx.strokeStyle = 'rgba(212, 175, 55, 0.4)';
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.moveTo(width / 2 - 300, 270);
    ctx.lineTo(width / 2 + 300, 270);
    ctx.stroke();

    // 3. Winner Crown Card Banner
    const winnerPlayer = gameData.players?.find(p => p.playerId === (gameData.winnerPlayerId || gameData.winnerId)) || gameData.players?.[0];
    
    // Card background
    ctx.fillStyle = 'rgba(15, 23, 42, 0.75)';
    ctx.strokeStyle = '#d4af37';
    ctx.lineWidth = 6;
    ctx.beginPath();
    ctx.roundRect(100, 310, width - 200, 320, 24);
    ctx.fill();
    ctx.stroke();

    // Crown Icon text
    ctx.font = '70px sans-serif';
    ctx.fillText('👑', width / 2, 385);

    // Champion Label
    ctx.font = 'bold 34px sans-serif';
    ctx.fillStyle = '#fbbf24';
    ctx.fillText('ŞAMPİYON KORDON AĞASI', width / 2, 435);

    // Winner Name
    ctx.font = '900 64px sans-serif';
    ctx.fillStyle = '#ffffff';
    ctx.fillText(winnerPlayer?.name || 'Ağa', width / 2, 515);

    // Winner Stats Tagline: Exact net worth calculation
    let winnerNetWorth = 0;
    if (gameData?.endGameAwards?.korfezAgasi?.winner === winnerPlayer?.name) {
      const parsedVal = parseInt((gameData.endGameAwards.korfezAgasi.value || '').replace(/[^0-9]/g, ''));
      if (!isNaN(parsedVal) && parsedVal > 0) {
        winnerNetWorth = parsedVal;
      }
    }

    if (!winnerNetWorth && winnerPlayer) {
      winnerNetWorth = winnerPlayer.money || 0;
      if (Array.isArray(winnerPlayer.properties)) {
        winnerPlayer.properties.forEach(pid => {
          const tile = propertiesData.find(b => b.id === pid);
          const propState = gameData.properties?.[pid];
          if (tile && propState) {
            if (propState.mortgaged) {
              winnerNetWorth += Math.floor((tile.price || 100) / 2);
            } else {
              winnerNetWorth += tile.price || 0;
              const totalHouses = (propState.houses || 0) + (propState.hotel ? 5 : 0);
              winnerNetWorth += totalHouses * (tile.houseCost || 100);
            }
          }
        });
      }
    }
    ctx.font = 'bold 38px sans-serif';
    ctx.fillStyle = '#34d399';
    ctx.fillText(`Toplam Servet: ₺${winnerNetWorth}`, width / 2, 580);

    // 4. Altın Boyoz Ödülleri Grid
    ctx.font = '900 48px sans-serif';
    ctx.fillStyle = '#fbbf24';
    ctx.fillText('🏅 ALTIN BOYOZ ÖDÜLLERİ', width / 2, 700);

    const awards = gameData.endGameAwards || {};
    const awardKeys = Object.keys(awards);
    
    const startY = 740;
    const cardWidth = 410;
    const cardHeight = 160;
    const gapX = 30;
    const gapY = 20;

    awardKeys.slice(0, 10).forEach((key, index) => {
      const col = index % 2;
      const row = Math.floor(index / 2);
      const x = 115 + col * (cardWidth + gapX);
      const y = startY + row * (cardHeight + gapY);

      const award = awards[key];

      // Award Item Box
      ctx.fillStyle = 'rgba(255, 255, 255, 0.04)';
      ctx.strokeStyle = 'rgba(212, 175, 55, 0.35)';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.roundRect(x, y, cardWidth, cardHeight, 16);
      ctx.fill();
      ctx.stroke();

      // Award Title
      ctx.textAlign = 'left';
      ctx.font = 'bold 30px sans-serif';
      ctx.fillStyle = '#fbbf24';
      ctx.fillText(`🏆 ${award.name}`, x + 16, y + 42);

      // Winner Name
      ctx.font = 'bold 26px sans-serif';
      ctx.fillStyle = '#34d399';
      ctx.fillText(`Ağa: ${award.winner || 'Yok'}`, x + 16, y + 84);

      // Stat Value Badge
      ctx.font = 'bold 28px sans-serif';
      ctx.fillStyle = '#38bdf8';
      ctx.fillText(`Skor: ${award.value || '-'}`, x + 16, y + 126);
    });

    // 5. Footer Watermark & Call-To-Action
    const footerY = 1680;
    
    // Divider
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.2)';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(100, footerY);
    ctx.lineTo(width - 100, footerY);
    ctx.stroke();

    ctx.textAlign = 'center';
    ctx.font = '900 44px sans-serif';
    ctx.fillStyle = '#ffffff';
    ctx.fillText('Sen de Kordon Ağası Ol! 🌊🎲', width / 2, footerY + 65);

    ctx.font = 'bold 34px sans-serif';
    ctx.fillStyle = '#fbbf24';
    ctx.fillText(gameBrand.domain || 'aga.portal.tr', width / 2, footerY + 120);

    // Save Data URL
    setDataUrl(canvas.toDataURL('image/png'));
  }, [gameData]);

  const handleDownload = () => {
    if (!dataUrl) return;
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = `kordon_agalari_skor_${Date.now()}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleShare = async () => {
    if (!dataUrl) return;

    try {
      // Convert data URL to Blob File
      const response = await fetch(dataUrl);
      const blob = await response.blob();
      const file = new File([blob], 'kordon_agalari_skor.png', { type: 'image/png' });

      if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: 'Kordon Ağaları Skor Kartım!',
          text: `Kordon Ağaları oyununu tamamladım! Sen de Kordon Ağası olmak için oyna: ${gameBrand.domain || 'aga.portal.tr'} 🎲🌊`
        });
      } else {
        handleDownload();
      }
    } catch (err) {
      console.error('Share error:', err);
      handleDownload();
    }
  };

  return createPortal(
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.85)',
      backdropFilter: 'blur(8px)',
      zIndex: 10010,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '16px',
      boxSizing: 'border-box'
    }}>
      {/* Hidden Offscreen Canvas for Rendering */}
      <canvas ref={canvasRef} style={{ display: 'none' }} />

      {/* Preview Card Dialog */}
      <div style={{
        background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 100%)',
        border: '2px solid rgba(212, 175, 55, 0.5)',
        borderRadius: '20px',
        maxWidth: '420px',
        width: '100%',
        maxHeight: '92vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '20px',
        boxSizing: 'border-box',
        position: 'relative',
        boxShadow: '0 20px 50px rgba(0,0,0,0.8)'
      }} className="animate-pop-in">
        {/* Close Button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            background: 'rgba(255,255,255,0.1)',
            border: 'none',
            color: '#fff',
            borderRadius: '50%',
            width: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer'
          }}
        >
          <X size={18} />
        </button>

        {/* Modal Header */}
        <div style={{ textAlign: 'center', marginBottom: '12px' }}>
          <h3 style={{ margin: 0, fontSize: '1.2rem', color: '#fbbf24', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
            <Sparkles size={18} /> Story Skor Kartı
          </h3>
          <p style={{ margin: '4px 0 0 0', fontSize: '0.75rem', color: '#94a3b8' }}>
            Instagram Story, Twitter ve WhatsApp'ta paylaş!
          </p>
        </div>

        {/* Image Preview Area */}
        <div style={{
          width: '100%',
          maxHeight: '55vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          overflow: 'hidden',
          borderRadius: '12px',
          border: '1px solid rgba(255,255,255,0.1)',
          background: '#000',
          marginBottom: '16px'
        }}>
          {dataUrl ? (
            <img
              src={dataUrl}
              alt="Story Preview"
              style={{
                maxWidth: '100%',
                maxHeight: '55vh',
                objectFit: 'contain'
              }}
            />
          ) : (
            <div style={{ padding: '40px', color: '#94a3b8' }}>Görsel Hazırlanıyor...</div>
          )}
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '10px', width: '100%' }}>
          <button
            onClick={handleShare}
            style={{
              flex: 1,
              background: 'linear-gradient(135deg, #d4af37 0%, #f59e0b 100%)',
              color: '#000',
              fontWeight: 800,
              fontSize: '0.9rem',
              border: 'none',
              borderRadius: '12px',
              padding: '12px 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              cursor: 'pointer',
              boxShadow: '0 4px 15px rgba(245, 158, 11, 0.4)'
            }}
          >
            <Share2 size={18} /> Paylaş
          </button>
          
          <button
            onClick={handleDownload}
            style={{
              background: 'rgba(255,255,255,0.1)',
              color: '#fff',
              fontWeight: 700,
              fontSize: '0.9rem',
              border: '1px solid rgba(255,255,255,0.2)',
              borderRadius: '12px',
              padding: '12px 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              cursor: 'pointer'
            }}
          >
            <Download size={18} /> İndir
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
